import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, Button, Space, message } from "antd";
import { ThunderboltOutlined } from "@ant-design/icons";
import { useTranslate, useApiUrl } from "@pankod/refine-core";
import { useSelect } from "@pankod/refine-antd";
import axios from "axios";
import { IQuiz, ILanguage, IQuizCategory } from "interfaces";
import KeywordTagsSelect from "components/keywordTagsSelect";

const { TextArea } = Input;

interface EditQuizModalProps {
  visible: boolean;
  quiz: IQuiz | null;
  creationModelString: string;
  onClose: () => void;
  onSavedAndApproved: () => void;
}

const EditQuizModal: React.FC<EditQuizModalProps> = ({
  visible,
  quiz,
  creationModelString,
  onClose,
  onSavedAndApproved,
}) => {
  const t = useTranslate();
  const apiUrl = useApiUrl();
  const [form] = Form.useForm();
  const [savingAndApproving, setSavingAndApproving] = useState(false);

  const { selectProps: quizCategorySelectProps } = useSelect<IQuizCategory>({
    resource: "quiz-category",
    optionLabel: "name",
    optionValue: "quiz_category_id",
  });

  const { selectProps: languageSelectProps } = useSelect<ILanguage>({
    resource: "language",
    optionLabel: "name",
    optionValue: "lang_code",
  });

  useEffect(() => {
    if (visible && quiz) {
      form.setFieldsValue({
        question: quiz.question,
        answer1: quiz.answer1,
        answer2: quiz.answer2,
        answer3: quiz.answer3,
        answer4: quiz.answer4,
        correct_answer: Number(quiz.correct_answer),
        level: Number(quiz.level),
        explanation: quiz.explanation,
        keywords: quiz.keywords || [],
        status: quiz.status,
        lang: quiz.lang,
        quiz_category_id: quiz.quiz_category_id,
      });
    }
  }, [visible, quiz, form]);

  const submit = async () => {
    if (!quiz) return;
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const APIToken = localStorage.getItem("api-token");
    const headers = { Authorization: `Bearer ${APIToken}` };

    setSavingAndApproving(true);

    try {
      const payload = {
        ...quiz,
        ...values,
        status: "enabled",
      };
      await axios.put(`${apiUrl}/quiz/${quiz.quiz_id}`, payload, {
        headers,
      });
      await axios.post(
        `${apiUrl}/quiz/${quiz.quiz_id}/update-children`,
        { model_string: creationModelString },
        { headers }
      );
      message.success(t("quiz-ai-creation.review.approved_translated"));
      onSavedAndApproved();
    } catch (err) {
      const msg =
        axios.isAxiosError(err) && err.response?.data?.message
          ? err.response.data.message
          : t("quiz-ai-creation.review.action_failed");
      message.error(msg);
    } finally {
      setSavingAndApproving(false);
    }
  };

  return (
    <Modal
      title={t("quiz-ai-creation.review.edit_title")}
      visible={visible}
      onCancel={onClose}
      width={720}
      destroyOnClose
      maskClosable={false}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={savingAndApproving}>
          {t("buttons.cancel")}
        </Button>,
        <Button
          key="save-approve"
          type="primary"
          icon={<ThunderboltOutlined />}
          loading={savingAndApproving}
          onClick={submit}
        >
          {t("quiz-ai-creation.review.save_approve_translate")}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={t("quiz.fields.question")}
          name="question"
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>
        <Space style={{ display: "flex" }} size="middle">
          <Form.Item
            label={t("quiz.fields.answer1")}
            name="answer1"
            rules={[{ required: true }]}
            style={{ flex: 1 }}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t("quiz.fields.answer2")}
            name="answer2"
            rules={[{ required: true }]}
            style={{ flex: 1 }}
          >
            <Input />
          </Form.Item>
        </Space>
        <Space style={{ display: "flex" }} size="middle">
          <Form.Item
            label={t("quiz.fields.answer3")}
            name="answer3"
            rules={[{ required: true }]}
            style={{ flex: 1 }}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t("quiz.fields.answer4")}
            name="answer4"
            rules={[{ required: true }]}
            style={{ flex: 1 }}
          >
            <Input />
          </Form.Item>
        </Space>
        <Form.Item
          label={t("quiz.fields.correct_answer")}
          name="correct_answer"
          rules={[{ required: true }]}
        >
          <Select
            options={[
              { label: 1, value: 1 },
              { label: 2, value: 2 },
              { label: 3, value: 3 },
              { label: 4, value: 4 },
            ]}
          />
        </Form.Item>
        <Form.Item
          label={t("quiz.fields.quiz_category_name")}
          name="quiz_category_id"
          rules={[{ required: true }]}
        >
          <Select {...quizCategorySelectProps} />
        </Form.Item>
        <Form.Item
          label={t("quiz.fields.lang")}
          name="lang"
          rules={[{ required: true }]}
        >
          <Select {...languageSelectProps} />
        </Form.Item>
        <Form.Item
          label={t("quiz.fields.explanation")}
          name="explanation"
          rules={[{ required: true }]}
        >
          <TextArea rows={5} />
        </Form.Item>
        <Form.Item label={t("quiz.fields.keywords")} name="keywords">
          <KeywordTagsSelect />
        </Form.Item>
        <Form.Item
          label={t("quiz.fields.level")}
          name="level"
          rules={[{ required: true }]}
        >
          <Select
            options={[
              { label: 1, value: 1 },
              { label: 2, value: 2 },
              { label: 3, value: 3 },
            ]}
          />
        </Form.Item>
        <Form.Item
          label={t("quiz.fields.status")}
          name="status"
          rules={[{ required: true }]}
        >
          <Select
            options={[
              { label: t("quiz.status.enabled"), value: "enabled" },
              { label: t("quiz.status.disabled"), value: "disabled" },
              { label: t("quiz.status.for_review"), value: "for_review" },
            ]}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditQuizModal;
